import { useCallback, useEffect, useRef, useState } from 'react';
import { getCalendarData, getCalendarDataBatch, CalendarData } from '../../api.pricingCalendar';

export interface MultiApartmentCalendarState {
  dataByApartmentId: Record<number, CalendarData | null>;
  loading: boolean;
  refreshApartment: (apartmentId: number) => Promise<void>;
}

/**
 * Fetches pricing/reservation data for the visible window in one batched request
 * (GET /api/apartments/booking-site/pricing-batch) instead of one call per apartment.
 */
export function useMultiApartmentCalendarData(
  apartmentIds: number[],
  from: string,
  to: string,
  token: string
): MultiApartmentCalendarState {
  const [dataByApartmentId, setDataByApartmentId] = useState<Record<number, CalendarData | null>>({});
  const [loading, setLoading] = useState(false);

  // Stable primitive key — an array's identity isn't stable across renders even with the same ids.
  const idsKey = apartmentIds.join(',');
  const latestRequest = useRef(0);

  useEffect(() => {
    if (!idsKey) { setDataByApartmentId({}); return; }
    const ids = idsKey.split(',').map(Number);
    const requestId = ++latestRequest.current;
    setLoading(true);

    getCalendarDataBatch(ids, from, to, token)
      .then(dataById => {
        if (latestRequest.current !== requestId) return; // a newer window/apartment-list request won the race
        // The endpoint silently drops any id the caller can't access — backfill those as null so
        // callers can still tell "no access/no data" apart from "not yet loaded".
        const merged: Record<number, CalendarData | null> = {};
        for (const id of ids) merged[id] = dataById[id] ?? null;
        setDataByApartmentId(merged);
        setLoading(false);
      })
      .catch(() => {
        if (latestRequest.current !== requestId) return;
        setDataByApartmentId(Object.fromEntries(ids.map(id => [id, null])));
        setLoading(false);
      });
  }, [idsKey, from, to, token]);

  const refreshApartment = useCallback(async (apartmentId: number) => {
    const data = await getCalendarData(apartmentId, from, to, token).catch(() => null);
    setDataByApartmentId(prev => ({ ...prev, [apartmentId]: data }));
  }, [from, to, token]);

  return { dataByApartmentId, loading, refreshApartment };
}
